import { TApiResponse, TApiParams, TApiTask } from "./types"
import BaseApi, { getQueryString, parameterizeArray } from "./base"
import _omit from "lodash/omit"
import _cloneDeep from "lodash/cloneDeep"

export type TApiTasksFilters = {
  name?: string
  state?: "archived" | "active"
  sectionStatus?: "active" | "archived" | "all"
  isBillable?: boolean
  tagIds?: string[]
  memberHashIds?: string[]
  groupHashIds?: string[]
}

export type TApiTasksSorting = {
  name?: "desc" | "asc"
}

export interface IGetAllTasksParams extends TApiParams {
  filters?: TApiTasksFilters
  sorting?: TApiTasksSorting
}

export interface IGetTasksResponse extends TApiResponse {
  tasks: Array<TApiTask>
}

export interface IGetCommonTasksResponse extends TApiResponse {
  bookmarked: Array<TApiTask>
  recentlyTracked: Array<TApiTask>
}

export interface ICreateTaskParams {
  name: string
  state?: "active" | "archived"
  createExternal?: boolean
  projectSectionId?: string
  projectSectionPosition?: number
  additionalDetails?: {
    parentId: string
    taskType: null | "to_do" | "card"
  }
}

export interface ICreateTaskResponse extends TApiResponse {
  task: TApiTask
}

export interface IUpdateTaskParams extends ICreateTaskParams {}

export interface IUpdateTaskResponse extends ICreateTaskResponse {}

export interface IGetProjectSectionPaginatedTasksParams extends TApiParams {
  projectId: string
  projectSectionId: string
  filters?: TApiTasksFilters
}

export interface IMoveTaskToProjectParams {
  projectId: string
  taskId: string
  newProjectId: string
}

class TasksApi extends BaseApi {
  async getTask(projectId: string, id: string) {
    return this.get(`/projects/${projectId}/tasks/${id}`)
  }

  async assignToTask(
    projectId: string,
    id: string,
    targets: { type: "user" | "group"; id: string }[]
  ) {
    const body = { targets: targets }
    return this.patch(`/projects/${projectId}/tasks/${id}/assign`, body)
  }

  async moveTaskToProject(params: IMoveTaskToProjectParams) {
    const {
      projectId,
      taskId,
      newProjectId
    } = params

    const body = { 
      task: {
        projectId: newProjectId
      }
     }

    return this.patch(`/projects/${projectId}/tasks/${taskId}/move`, body)
  }

  // TODO: remove
  async updateTasksWithDifferentSections(
    projectId: string,
    projectSectionId: string,
    projectSectionPosition: number,
    id: string,
  ) {
    const body = {
      projectSectionId,
      projectSectionPosition,
    }
    return this.patch(`/projects/${projectId}/tasks/${id}`, body)
  }

  async updateTaskPosition(props: {
    projectId: string,
    projectSectionId: string,
    position: number,
    taskId: string,
  }) {
    const body = {
      projectSectionId: props.projectSectionId,
      projectSectionPosition: props.position,
    }

    return this.patch(`/projects/${props.projectId}/tasks/${props.taskId}`, body)
  }

  async getProjectSectionPaginatedTasks(params: IGetProjectSectionPaginatedTasksParams) {
    const {
      perPage,
      page,
      filters,
      projectId,
      projectSectionId,
    } = params

    const query = getQueryString(
      this.paramsToSnakeCase({
        per_page: perPage || 20,
        page: page || 1,
        q: { s: ["project_section_position ASC"] },
        filters: filters || {}
      }),
      true
    )

    const response = this.get(
      `/projects/${projectId}/project_sections/${projectSectionId}/tasks/project_section_list${query}`
    )

    return this.getResponseJson(response)
  }

  async getAllTasks(
    projectId: string,
    params?: IGetAllTasksParams
  ): Promise<IGetTasksResponse> {
    let queryParams =
      params === undefined
        ? params
        : {
            q: { s: this.prepareSortingParams(params.sorting) },
            ..._omit(params, ["sorting"]),
          }
    const query = getQueryString(this.paramsToSnakeCase(queryParams), true)

    return this.get(`/projects/${projectId}/tasks/all${query}`)
  }

  async getTasks(
    projectId: string,
    requestParams?: IGetAllTasksParams
  ): Promise<IGetTasksResponse> {
    let params = _cloneDeep(requestParams) || {}

    if (params && params.filters && params.filters.hasOwnProperty("name")) {
      if (!params.filters.name) {
        params.filters.name = ""
      }
    }

    if (!params?.perPage) {
      params.perPage = 100
    }

    const query = getQueryString(this.paramsToSnakeCase(params), true)

    return this.get(`/projects/${projectId}/tasks${query}`)
  }

  async getCommonTasks(projectId: string): Promise<IGetCommonTasksResponse> {
    return this.get(`/projects/${projectId}/tasks/common`)
  }

  async bookmarkTask(projectId: string, taskId: string) {
    return this.patch(`/projects/${projectId}/tasks/${taskId}/bookmark`, {})
  }

  async unbookmarkTask(projectId: string, taskId: string) {
    return this.patch(`/projects/${projectId}/tasks/${taskId}/unbookmark`, {})
  }

  async createTask(
    projectId: string,
    params: ICreateTaskParams
  ): Promise<ICreateTaskResponse> {
    return this.post(`/projects/${projectId}/tasks`, {
      task: this.paramsToSnakeCase(params),
    })
  }

  async updateTask(
    projectId: string,
    taskId: string,
    params: Partial<TApiTask>
  ): Promise<IUpdateTaskResponse> {
    return this.patch(`/projects/${projectId}/tasks/${taskId}`, {
      task: this.paramsToSnakeCase(params),
    })
  }

  async archiveTask(
    projectId: string,
    taskId: string
  ): Promise<IUpdateTaskResponse> {
    return this.updateTask(projectId, taskId, {
      state: "archived",
    })
  }

  async restoreTask(
    projectId: string,
    taskId: string
  ): Promise<IUpdateTaskResponse> {
    return this.updateTask(projectId, taskId, {
      state: "active",
    })
  }

  async deleteTask(projectId: string, taskId: string): Promise<TApiResponse> {
    return this.delete(`/projects/${projectId}/tasks/${taskId}`)
  }
}

export default TasksApi
